import React, { useContext, useMemo } from 'react';
import { Card, CardHeader, CardContent, Box } from '@mui/material';
import { Bar, Pie } from 'react-chartjs-2';
import { LocalizeContext, LocalizeContextProps, Translate } from 'react-localize-redux';
import { chartColors } from '../config/analyticsCharts';
import { getCategoryColor } from '../utils/categoryColors';
import {
  AGE_BUCKETS,
  DemographicPatient,
  SEX_FEMALE,
  SEX_MALE,
  SEX_UNKNOWN,
  UNKNOWN_AGE_BUCKET,
  bucketAges,
  sexDistribution,
} from '../utils/demographics';


interface ChartProps {
  data: DemographicPatient[];
}

// AgeGroupChart Component
export const AgeGroupChart: React.FC<ChartProps> = ({ data: patients }) => {
  // SHER-828: getTranslate is a selector, not a hook, and LocalizeContext is missing from the
  // package's typings. Chart.js wants plain strings for labels, so they cannot be <Translate/>.
  const { translate } = useContext(LocalizeContext as React.Context<LocalizeContextProps>);

  const ageGroupData = useMemo(() => {
    // Bucketing lives in utils/demographics so it can be unit-tested against ages rather than
    // against a rendered canvas. The version inlined here overlapped at 75, silently dropped a
    // patient whose birthdate would not parse, and read age as thisYear - birthYear.
    const counts = bucketAges(patients);
    // The range ids are the labels; only the unknown row is translated.
    const labels = [
      ...AGE_BUCKETS.map((bucket) => bucket.id as string),
      String(translate('hospital.analytics.demographics.unknown')),
    ];

    const values = [
      ...AGE_BUCKETS.map((bucket) => counts[bucket.id]),
      counts[UNKNOWN_AGE_BUCKET],
    ];
    const total = values.reduce((sum, count) => sum + count, 0);

    return {
      labels,
      datasets: [{
        label: String(translate('hospital.analytics.demographics.percentage_of_patients')),
        data: values.map((value) => (total > 0 ? Math.round((value / total) * 100) : 0)),
        backgroundColor: `${chartColors.seriesColors[0]}80`, // Single color with transparency
        borderColor: chartColors.seriesColors[0], // Single color for border
        borderWidth: 1,
        // Store original counts as metadata for tooltip
        originalData: values
      }]
    };
  }, [patients, translate]);

  const options: {
    responsive: boolean;
    maintainAspectRatio: boolean;
    scales: {
      y: {
        beginAtZero: boolean;
        max?: number;
        ticks: {
          stepSize?: number;
          callback?: (value: number) => string;
        };
      };
    };
    plugins: {
      legend: {
        position: 'top';
      };
      title: {
        display: boolean;
      };
      tooltip: {
        callbacks: {
          label: (context: any) => string;
        };
      };
    };
  } = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: (value) => `${value}%`
        }
      }
    },
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            // Display the percentage with 1 decimal place
            const percentage = parseFloat(context.raw as string).toFixed(1);
            // Get the original count from the metadata
            const index = context.dataIndex;
            const count = (context.dataset as any).originalData[index];

            return `${context.dataset.label}: ${percentage}% (${count} ${translate('hospital.analytics.demographics.patients')})`;
          }
        }
      }
    }
  };

  return (
    <Card>
      <CardHeader title={<Translate id="hospital.analytics.demographics.age_distribution" />} />
      <CardContent>
        <Box height={300}>
          <Bar data={ageGroupData} options={options} />
        </Box>
      </CardContent>
    </Card>
  );
};

// SexDistributionChart Component
export const SexDistributionChart: React.FC<ChartProps> = ({ data: patients }) => {
  const { translate } = useContext(LocalizeContext as React.Context<LocalizeContextProps>);

  const genderData = useMemo(() => {
    // Three slices, not two. Reading only Male and Female made a patient recorded as anything
    // else vanish from the chart AND from the denominator the tooltip divides by, so the pie
    // summed to 100% over a population smaller than the card claimed.
    const counts = sexDistribution(patients);
    // Colours come from `getCategoryColor`, the tab's declared owner of "this category is always
    // this colour". Hardcoding them here had already drifted: Female was amber in this pie and
    // red everywhere else on the same dashboard.
    const slices = [
      { key: SEX_MALE, id: 'hospital.analytics.demographics.male' },
      { key: SEX_FEMALE, id: 'hospital.analytics.demographics.female' },
      { key: SEX_UNKNOWN, id: 'hospital.analytics.demographics.unknown' },
    ] as const;

    return {
      labels: slices.map((slice) => String(translate(slice.id))),
      datasets: [{
        data: slices.map((slice) => counts[slice.key]),
        backgroundColor: slices.map((slice) => `${getCategoryColor(slice.key)}80`),
        borderColor: slices.map((slice) => getCategoryColor(slice.key)),
        borderWidth: 1,
      }]
    };
  }, [patients, translate]);

  const options: {
    responsive: boolean;
    maintainAspectRatio: boolean;
    plugins: {
      legend: {
        position: 'top';
      };
      tooltip: {
        callbacks: {
          label: (context: any) => string;
        };
      };
    };
  } = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const total = genderData.datasets[0].data.reduce((a, b) => a + b, 0);
            const percentage = ((context.raw / total) * 100).toFixed(1);
            return `${context.label}: ${context.raw} (${percentage}%)`;
          }
        }
      }
    }
  };

  return (
    <Card>
      <CardHeader title={<Translate id="hospital.analytics.demographics.sex_distribution" />} />
      <CardContent>
        <Box height={300}>
          <Pie data={genderData} options={options} />
        </Box>
      </CardContent>
    </Card>
  );
};